import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Snackbar, Alert, CardContent, Typography, Box, Tooltip, IconButton, Button } from '@mui/material';
import { AddAPhoto, Delete, CheckCircle, Cancel } from '@mui/icons-material';
import { EditorState, convertToRaw, convertFromHTML, ContentState } from 'draft-js';
import { convertToHTML } from 'draft-convert';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from 'config/api';
import { REACT_APP_GOOGLE_MAPS_API_KEY } from 'config/keys';
import { updateVenue, uploadVenueImage } from 'api';
import GeneralInfoView from './view';
import { HoneyImageCard, HoneyImageUploadArea, HoneyImagePreview, HoneyImageDeleteButton } from './styled';

const GeneralInfoContainer = ({ venueData, onVenueUpdate }) => {
  const { t } = useTranslation();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    single_address: '',
    website: '',
    contact_email: '',
    contact_phone_1: '',
    contact_phone_2: '',
    twitter_url: '',
    facebook_url: '',
    instagram_url: '',
    linkedin_url: '',
    occupancy_instant_booking: 0,
    latitude: null,
    longitude: null,
    status: 'PP',
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Editor states
  const [descriptionEditorState, setDescriptionEditorState] = useState(EditorState.createEmpty());
  const [accessEditorState, setAccessEditorState] = useState(EditorState.createEmpty());
  
  // Image states
  const [selectedBackground, setSelectedBackground] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [backgroundPreview, setBackgroundPreview] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Snackbar helper function
  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Initialize form data from venue data
  useEffect(() => {
    const parseCoordinate = (value) => {
      if (value === null || value === undefined) return null;
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const parsed = parseFloat(value);
        return isNaN(parsed) ? null : parsed;
      }
      return null;
    };

    const latitude = parseCoordinate(venueData.latitude);
    const longitude = parseCoordinate(venueData.longitude);

    setFormData({
      name: venueData.name || '',
      description: venueData.description || '',
      single_address: venueData.single_address || '',
      website: venueData.website || '',
      contact_email: venueData.contact_email || '',
      contact_phone_1: venueData.contact_phone_1 || '',
      contact_phone_2: venueData.contact_phone_2 || '',
      twitter_url: venueData.twitter_url || '',
      facebook_url: venueData.facebook_url || '',
      instagram_url: venueData.instagram_url || '',
      linkedin_url: venueData.linkedin_url || '',
      occupancy_instant_booking: venueData.occupancy_instant_booking || 0,
      latitude: latitude,
      longitude: longitude,
      status: venueData.status || 'PP',
    });

    // Set initial image previews
    if (venueData.image) {
      setImagePreview(venueData.image.startsWith('http') ? venueData.image : `${API_BASE_URL}${venueData.image}`);
    }

    if (venueData.background) {
      setBackgroundPreview(venueData.background.startsWith('http') ? venueData.background : `${API_BASE_URL}${venueData.background}`);
    }

    // Initialize editor states
    if (venueData.full_description) {
      const blocksFromHTML = convertFromHTML(venueData.full_description);
      const state = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap,
      );
      setDescriptionEditorState(EditorState.createWithContent(state));
    }

    if (venueData.access) {
      const blocksFromHTML = convertFromHTML(venueData.access);
      const state = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap,
      );
      setAccessEditorState(EditorState.createWithContent(state));
    }
  }, [venueData]);

  // Handle input changes
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  // Handle slider change
  const handleSliderChange = useCallback((event, newValue) => {
    setFormData(prev => ({ ...prev, occupancy_instant_booking: newValue / 100 }));
  }, []);

  // Handle status change
  const handleStatusChange = useCallback((event, newStatus) => {
    if (newStatus !== null) {
      setFormData(prev => ({ ...prev, status: newStatus }));
    }
  }, []);

  // Handle coordinates change
  const handleCoordinatesChange = useCallback((coordinates, address) => {
    setFormData(prev => ({
      ...prev,
      latitude: typeof coordinates.lat === 'string' ? parseFloat(coordinates.lat) : coordinates.lat,
      longitude: typeof coordinates.lng === 'string' ? parseFloat(coordinates.lng) : coordinates.lng,
      single_address: address && !prev.single_address ? address : prev.single_address
    }));
  }, []);

  // Handle editor state changes
  const handleEditorStateChange = useCallback((editorState) => {
    if (editorState) {
      setDescriptionEditorState(editorState);
    }
  }, []);

  const handleAccessEditorStateChange = useCallback((editorState) => {
    if (editorState) {
      setAccessEditorState(editorState);
    }
  }, []);

  // Handle image uploads
  const handleImageChange = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      setSelectedImage(file);
    }
  }, []);

  const handleBackgroundChange = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        setBackgroundPreview(reader.result);
      };
      setSelectedBackground(file);
    }
  }, []);

  const clearImageSelection = useCallback(() => {
    setSelectedImage(null);
    setImagePreview(venueData.image ? 
      (venueData.image.startsWith('http') ? venueData.image : `${API_BASE_URL}${venueData.image}`) 
      : null);
  }, [venueData.image]);

  const clearBackgroundSelection = useCallback(() => {
    setSelectedBackground(null);
    setBackgroundPreview(venueData.background ? 
      (venueData.background.startsWith('http') ? venueData.background : `${API_BASE_URL}${venueData.background}`) 
      : null);
  }, [venueData.background]);

  // Image upload handlers
  const handleUploadBackground = useCallback(async () => {
    if (selectedBackground) {
      try {
        await uploadVenueImage(venueData?.slug, selectedBackground, 'background');
        showSnackbar('Background image uploaded successfully', 'success');
        if (onVenueUpdate) onVenueUpdate();
      } catch (error) {
        console.error('Error uploading background:', error);
        showSnackbar('Failed to upload background image', 'error');
      }
    }
  }, [selectedBackground, venueData?.slug, showSnackbar, onVenueUpdate]);

  const handleUploadImage = useCallback(async () => {
    if (selectedImage) {
      try {
        await uploadVenueImage(venueData?.slug, selectedImage, 'image');
        showSnackbar('Main image uploaded successfully', 'success');
        if (onVenueUpdate) onVenueUpdate();
      } catch (error) {
        console.error('Error uploading image:', error);
        showSnackbar('Failed to upload main image', 'error');
      }
    }
  }, [selectedImage, venueData?.slug, showSnackbar, onVenueUpdate]);

  // Auto-upload images when selected
  useEffect(() => {
    if (selectedBackground !== null) {
      handleUploadBackground();
    }
  }, [selectedBackground, handleUploadBackground]);

  useEffect(() => {
    if (selectedImage !== null) {
      handleUploadImage();
    }
  }, [selectedImage, handleUploadImage]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setError('');

    const htmlDescription = descriptionEditorState ? convertToHTML(descriptionEditorState.getCurrentContent()) : '';
    const htmlAccess = accessEditorState ? convertToHTML(accessEditorState.getCurrentContent()) : '';

    const updateData = {
      slug: venueData?.slug,
      name: formData.name,
      description: formData.description,
      full_description: htmlDescription,
      single_address: formData.single_address,
      website: formData.website,
      contact_email: formData.contact_email,
      contact_phone_1: formData.contact_phone_1,
      contact_phone_2: formData.contact_phone_2,
      access: htmlAccess,
      twitter_url: formData.twitter_url,
      facebook_url: formData.facebook_url,
      instagram_url: formData.instagram_url,
      linkedin_url: formData.linkedin_url,
      occupancy_instant_booking: formData.occupancy_instant_booking,
      latitude: formData.latitude,
      longitude: formData.longitude,
      status: formData.status,
    };

    try {
      await updateVenue(updateData);
      showSnackbar('Venue information updated successfully', 'success');
      // Refresh venue data in parent to update the checklist
      if (onVenueUpdate) {
        onVenueUpdate();
      }
    } catch (error) {
      console.error('Update error:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.detail || error.message || 'An error occurred while updating the venue.';
      const errorCode = error.response?.status ? ` (Error ${error.response.status})` : '';
      setError(errorMessage);
      showSnackbar(`Failed to update venue information${errorCode}: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [formData, descriptionEditorState, accessEditorState, venueData?.slug, showSnackbar]);

  // Helper function for status info
  const getStatusInfo = useCallback((status) => {
    switch (status) {
      case 'AC':
        return { label: 'Active', color: 'success', icon: <CheckCircle /> };
      case 'IN':
        return { label: 'Inactive', color: 'error', icon: <Cancel /> };
      case 'PP':
        return { label: 'Pending', color: 'warning', icon: <Cancel /> };
      case 'PA':
        return { label: 'Pre-Approved', color: 'info', icon: <CheckCircle /> };
      default:
        return { label: 'Unknown', color: 'default', icon: <Cancel /> };
    }
  }, []);

  // Render image upload card
  const renderImageUploadCard = useCallback((title, preview, handleChange, clearSelection, inputId) => (
    <HoneyImageCard>
      <CardContent>
        <Typography variant="h6" gutterBottom>{title}</Typography>

        {preview ? (
          <Box sx={{ position: 'relative' }}>
            <HoneyImagePreview
              component="img"
              image={preview}
              alt={title}
            />
            <HoneyImageDeleteButton onClick={clearSelection} size="small">
              <Tooltip title="Remove image">
                <Delete />
              </Tooltip>
            </HoneyImageDeleteButton>
          </Box>
        ) : (
          <HoneyImageUploadArea onClick={() => document.getElementById(inputId).click()}>
            <Box sx={{ textAlign: 'center' }}>
              <AddAPhoto sx={{ fontSize: 40, color: '#999', mb: 1 }} />
              <Typography variant="body2" color="textSecondary">
                Click to upload
              </Typography>
            </Box>
          </HoneyImageUploadArea>
        )}

        <input
          type="file"
          id={inputId}
          accept="image/*"
          onChange={handleChange}
          style={{ display: 'none' }}
        />

        <Button
          variant="outlined"
          onClick={() => document.getElementById(inputId).click()}
          fullWidth
          sx={{ mt: 1 }}
        >
          {preview ? 'Change Image' : 'Select Image'}
        </Button>
      </CardContent>
    </HoneyImageCard>
  ), []);

  // Memoized values
  const apiKey = useMemo(() => REACT_APP_GOOGLE_MAPS_API_KEY, []);

  return (
    <>
      <GeneralInfoView
        // Form data
        formData={formData}
        loading={loading}
        error={error}
        
        // Editor states
        descriptionEditorState={descriptionEditorState}
        accessEditorState={accessEditorState}
        
        // Image states
        imagePreview={imagePreview}
        backgroundPreview={backgroundPreview}
        
        // Handlers
        handleInputChange={handleInputChange}
        handleSliderChange={handleSliderChange}
        handleStatusChange={handleStatusChange}
        handleEditorStateChange={handleEditorStateChange}
        handleAccessEditorStateChange={handleAccessEditorStateChange}
        handleImageChange={handleImageChange}
        handleBackgroundChange={handleBackgroundChange}
        clearImageSelection={clearImageSelection}
        clearBackgroundSelection={clearBackgroundSelection}
        handleCoordinatesChange={handleCoordinatesChange}
        handleSubmit={handleSubmit}
        
        // Helper functions
        getStatusInfo={getStatusInfo}
        renderImageUploadCard={renderImageUploadCard}
        
        // External props
        apiKey={apiKey}
        t={t}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GeneralInfoContainer;